import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  message,
} from 'antd';
import { API } from '@/services/api.service';

const KIND_COLOR = { BLOCK: 'purple', UNIT: 'geekblue', CODE: 'green' };

/**
 * The translation table itself. A source value the journal carries that is not
 * listed here does not silently vanish — the run reports it — but this is where
 * it gets a home.
 *
 * @param {Object} props - Component props
 * @param {string} props.packageId - Package whose mappings are shown
 * @returns {JSX.Element} The mappings table
 */
export default function MappingsPanel({ packageId }) {
  const { t } = useTranslation(['tables', 'forms', 'messages']);
  const gr = useCallback(
    (key) => t(`groupReporting.${key}`, { ns: 'tables' }),
    [t]
  );
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const { data, isLoading } = useQuery({
    queryKey: ['group-reporting-mappings', packageId],
    queryFn: () => API.groupReporting.getDimensionMaps({ packageId }),
    enabled: Boolean(packageId),
  });

  const mappings = data?.data || [];

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: ['group-reporting-mappings', packageId],
    });

  const saveMutation = useMutation({
    mutationFn: (values) =>
      editing?._id
        ? API.groupReporting.updateDimensionMap(editing._id, values)
        : API.groupReporting.createDimensionMap({ ...values, packageId }),
    onSuccess: () => {
      setEditing(null);
      form.resetFields();
      invalidate();
      message.success(
        t(editing?._id ? 'updateSuccess' : 'createSuccess', { ns: 'messages' })
      );
    },
    onError: (error) =>
      message.error(error?.response?.data?.message || error.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (mapId) => API.groupReporting.deleteDimensionMap(mapId),
    onSuccess: () => {
      invalidate();
      message.success(t('deleteSuccess', { ns: 'messages' }));
    },
    onError: (error) =>
      message.error(error?.response?.data?.message || error.message),
  });

  const classLabel = useCallback(
    (sourceClass) => {
      if (sourceClass === 5) return gr('classCosts');
      if (sourceClass === 6) return gr('classRevenue');
      return gr('bothClasses');
    },
    [gr]
  );

  const columns = useMemo(
    () => [
      {
        title: gr('kind'),
        dataIndex: 'kind',
        key: 'kind',
        width: 110,
        filters: ['BLOCK', 'UNIT', 'CODE'].map((kind) => ({
          text: kind,
          value: kind,
        })),
        onFilter: (value, row) => row.kind === value,
        render: (kind) => <Tag color={KIND_COLOR[kind]}>{kind}</Tag>,
      },
      {
        title: gr('sourceValue'),
        dataIndex: 'sourceValue',
        key: 'sourceValue',
        width: 150,
      },
      {
        title: gr('sourceClass'),
        dataIndex: 'sourceClass',
        key: 'sourceClass',
        width: 140,
        render: classLabel,
      },
      {
        title: gr('targetValue'),
        dataIndex: 'targetValue',
        key: 'targetValue',
        width: 150,
      },
      {
        title: gr('targetLabel'),
        dataIndex: 'targetLabel',
        key: 'targetLabel',
      },
      { title: gr('notes'), dataIndex: 'notes', key: 'notes' },
      {
        title: '',
        key: 'actions',
        width: 100,
        render: (_, row) => (
          <Space>
            <Button
              size="small"
              className="!rounded-[var(--buttonRadius)]"
              icon={<EditOutlined />}
              onClick={() => {
                setEditing(row);
                form.setFieldsValue(row);
              }}
            />
            <Popconfirm
              title={gr('confirmDelete')}
              onConfirm={() => deleteMutation.mutate(row._id)}
            >
              <Button
                size="small"
                danger
                className="!rounded-[var(--buttonRadius)]"
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [classLabel, deleteMutation, form, gr]
  );

  return (
    <Card
      size="small"
      extra={
        <Button
          type="primary"
          className="!rounded-[var(--buttonRadius)]"
          icon={<PlusOutlined />}
          disabled={!packageId}
          onClick={() => {
            setEditing({});
            form.resetFields();
          }}
        >
          {gr('addMapping')}
        </Button>
      }
    >
      <Table
        size="small"
        rowKey="_id"
        loading={isLoading}
        columns={columns}
        dataSource={mappings}
        locale={{ emptyText: gr('noMappings') }}
        pagination={{ pageSize: 25, showSizeChanger: true }}
        scroll={{ x: 'max-content' }}
      />

      <Modal
        open={Boolean(editing)}
        title={editing?._id ? gr('editMapping') : gr('addMapping')}
        onCancel={() => setEditing(null)}
        onOk={() => form.submit()}
        confirmLoading={saveMutation.isPending}
        destroyOnHidden
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => saveMutation.mutate(values)}
        >
          <Form.Item
            name="kind"
            label={gr('kind')}
            rules={[{ required: true }]}
          >
            <Select
              options={['BLOCK', 'UNIT', 'CODE'].map((kind) => ({
                value: kind,
                label: kind,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="sourceValue"
            label={gr('sourceValue')}
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="sourceClass" label={gr('sourceClass')}>
            <Select
              allowClear
              options={[
                { value: 5, label: gr('classCosts') },
                { value: 6, label: gr('classRevenue') },
              ]}
              placeholder={gr('bothClasses')}
            />
          </Form.Item>
          <Form.Item
            name="targetValue"
            label={gr('targetValue')}
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="targetLabel" label={gr('targetLabel')}>
            <Input />
          </Form.Item>
          <Form.Item name="notes" label={gr('notes')}>
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
